import { TextField } from '@react-spectrum/s2';
import { useEffect, useState } from 'react';

import { resolveTarget, readPropertyValue } from '../targetResolvers.ts';
import type { EditPatch, EditableDescriptor } from '../types.ts';

import { ColorPicker } from './ColorPicker.tsx';

type ButtonPopupProps = {
	descriptor: EditableDescriptor;
	iframeDoc: Document;
	brandColors?: string[] | null;
	onApplyEdit: (
		descriptor: EditableDescriptor,
		patch: Partial<EditPatch>,
	) => void;
};

export const ButtonPopup = ({
	descriptor,
	iframeDoc,
	brandColors,
	onApplyEdit,
}: ButtonPopupProps) => {
	const element = iframeDoc.getElementById(descriptor.id);
	const currentText = element
		? readPropertyValue(element, 'btn', 'textContent') || ''
		: '';
	const currentTextColor = element
		? readPropertyValue(element, 'btn', 'textColor') || '#000000'
		: '#000000';
	const currentBg = element
		? readPropertyValue(element, 'btn', 'backgroundColor') || '#ffffff'
		: '#ffffff';
	const currentBorder = element
		? readPropertyValue(element, 'btn', 'borderColor') || '#000000'
		: '#000000';
	const hasAnchor = element
		? resolveTarget(element, 'btn', 'href') !== null
		: false;
	const currentHref = element
		? readPropertyValue(element, 'btn', 'href') || ''
		: '';

	const [buttonText, setButtonText] = useState(currentText);
	const [textHex, setTextHex] = useState(currentTextColor);
	const [fillHex, setFillHex] = useState(currentBg);
	const [borderHex, setBorderHex] = useState(currentBorder);

	// Resync when switching to a different element
	useEffect(() => {
		setButtonText(currentText);
		setTextHex(currentTextColor);
		setFillHex(currentBg);
		setBorderHex(currentBorder);
	}, [descriptor.id]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div className="flex flex-col gap-3">
			<div
				className="text-xs font-medium tracking-wider text-gray-500 uppercase"
			>
				Button
			</div>

			<TextField
				label="Button text"
				value={buttonText}
				onChange={(value) => {
					setButtonText(value);
					onApplyEdit(descriptor, { textContent: value });
				}}
			/>

			<ColorPicker
				label="Text Color"
				value={textHex}
				onChange={(hex) => {
					setTextHex(hex);
					onApplyEdit(descriptor, { textColor: hex });
				}}
				brandColors={brandColors}
			/>

			<ColorPicker
				label="Fill Color"
				value={fillHex}
				onChange={(hex) => {
					setFillHex(hex);
					onApplyEdit(descriptor, { backgroundColor: hex });
				}}
				brandColors={brandColors}
			/>

			<ColorPicker
				label="Border Color"
				value={borderHex}
				onChange={(hex) => {
					setBorderHex(hex);
					onApplyEdit(descriptor, { borderColor: hex });
				}}
			/>

			{hasAnchor && (
				<TextField
					label="Link URL"
					defaultValue={currentHref}
					onChange={(value) => onApplyEdit(descriptor, { href: value })}
				/>
			)}
		</div>
	);
};
